import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import pdfIcon from "../../assets/pdf.png";
import regenaiLogo from "../../assets/regenai-logo.png";
import {
  Container,
  Typography,
  Paper,
  Box,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import { deletePdf } from "../../store/slices/pdfSlice";
import { useTheme } from "@mui/material/styles";
import RegeneratePdf from "./RegeneratePdf";
import { motion } from "framer-motion";
import { MdOpenInNew, MdDownload, MdDelete } from "react-icons/md";
import { toggleRightSection } from "../../store/slices/sectionSlice";
interface PdfFile {
  id: string;
  filename: string;
}

const PdfViewer = () => {
  const dispatch = useDispatch();
  const pdfFiles = useSelector((state: RootState) => state.pdf.currentChatFiles);
  const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup);
  const [selectedFile, setSelectedFile] = useState<PdfFile | null>(null);
  const [modalKey, setModalKey] = useState(0);
  const theme = useTheme();
  const [isRegenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const baseUrl = import.meta.env.VITE_FILES_BASE_URL;

  // Switch PDF files when active chat changes
  useEffect(() => {
    if (activeChatGroup) {
      dispatch({ type: 'pdf/switchToChatPdfs', payload: activeChatGroup });
    } else {
      dispatch({ type: 'pdf/clearCurrentChatPdfs' });
    }
  }, [activeChatGroup, dispatch]);
  // Add new effect to handle right section visibility when PDFs change
  useEffect(() => {
    if (activeChatGroup) {
      // If there are PDFs, open the right section
      if (pdfFiles.length > 0) {
        dispatch(toggleRightSection(true));
      } else {
        dispatch(toggleRightSection(false));
      }
    }
  }, [activeChatGroup, pdfFiles, dispatch]);

  const handleDownload = async (filename: string) => {
    const downloadUrl = `${baseUrl}download/${filename}`;
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading the file", error);
    }
  };

  const handleOpen = (filename: string) => {
    const openUrl = `${baseUrl}open/${filename}`;
    window.open(openUrl, "_blank");
  };

  const handleDelete = (id: string) => {
    if (activeChatGroup) {
      dispatch(deletePdf({ fileId: id, chatGroupId: activeChatGroup }));
    }
  };

  const handleRegenerate = (file: PdfFile) => {
    setSelectedFile(file);
    setRegenerateModalOpen(true);
    setModalKey((prevKey) => prevKey + 1);
  };

  const handleCloseRegenerateModal = () => {
    setRegenerateModalOpen(false);
    setSelectedFile(null);
  };

  if (pdfFiles.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={1}
      sx={{
        width: "90%",
        maxWidth: "250px",
        margin: "0 auto",
        height: "100%",
        overflow: "hidden",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "8px",
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: theme.palette.mode === "dark" 
            ? "rgba(255, 255, 255, 0.2)" 
            : "rgba(0, 0, 0, 0.2)",
          borderRadius: "4px",
          "&:hover": {
            background: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          },
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
      }}
    >
      {pdfFiles.map((file: PdfFile, index: number) => (
        <Paper
          key={file.id}
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
          elevation={0}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(30, 30, 30, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(12px)",
            borderRadius: "12px",
            padding: "20px",
            minHeight: "120px",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                : "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.08)"
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              mb: 2,
            }}
          >
            <img
              src={pdfIcon}
              alt="PDF"
              style={{
                width: "32px",
                height: "32px",
                flexShrink: 0,
                filter:
                  theme.palette.mode === "dark" ? "brightness(0.9)" : "none",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: theme.palette.text.primary,
                fontWeight: 500,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.2em",
                maxHeight: "2.4em",
              }}
              title={file.filename}
            >
              {file.filename}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <Tooltip title="Open">
              <IconButton
                onClick={() => handleOpen(file.filename)}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138, 43, 226, 0.15)"
                        : "rgba(138, 43, 226, 0.08)",
                  },
                }}
              >
                <MdOpenInNew size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                onClick={() => handleDownload(file.filename)}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138, 43, 226, 0.15)"
                        : "rgba(138, 43, 226, 0.08)",
                  },
                }}
              >
                <MdDownload size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Regenerate">
              <IconButton
                onClick={() => handleRegenerate(file)}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138, 43, 226, 0.15)"
                        : "rgba(138, 43, 226, 0.08)",
                  },
                  padding: "4px",
                }}
              >
                <img
                  src={regenaiLogo}
                  alt="Regenerate"
                  style={{
                    width: "18px",
                    height: "18px",
                    objectFit: "contain",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleDelete(file.id)}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: "#ff3b30",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 59, 48, 0.15)"
                        : "rgba(255, 59, 48, 0.08)",
                  },
                }}
              >
                <MdDelete size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      ))}
      {selectedFile && (
        <RegeneratePdf
          key={modalKey}
          open={isRegenerateModalOpen}
          onClose={handleCloseRegenerateModal}
          filename={selectedFile.filename}
          baseUrl={baseUrl}
          licenseKey={import.meta.env.VITE_APRYSE_LICENSE_KEY}
        />
      )}
    </Stack>
  );
};

export default PdfViewer;
